<template>
    <div id="new-meta-insights-channel-form">
        <a-form>
            <div class="form-wrapper">
                <div class="input-wrapper d-none">
                    <label>{{ $t("whatsappChannel.channelName") }}</label>
                    <a-form-item :validate-status="getErrorStatus('channelName')" :help="getErrorMessage('channelName')">
                        <a-input
                            v-model="form.channelName"
                            :placeholder="$t('whatsappChannel.channelName')"
                            name="channelName"
                            ref="channelName"
                        />
                    </a-form-item>
                </div>

                <!-- Step 1: OAuth bekleniyor -->
                <div class="connect-body" v-if="step === 'oauth'">
                    <div class="requirements">
                        <div><i class="material-icons">info</i>{{ $t("message.ensureFacebookAccount") }}</div>
                        <div><i class="material-icons">info</i>{{ $t("message.ensureAdAccount") }}</div>
                        <div><i class="material-icons">info</i>{{ $t("metaInsights.requireAdsReadScope") }}</div>
                    </div>

                    <strong>{{ $t("metaInsights.connectDescription") }}</strong>

                    <div>
                        <a-checkbox :checked="termOfService" @change="acceptTermOfService('pp')"></a-checkbox>
                        <b
                            >{{ $t("search.integration.readAndAgree") }}
                            <a @click="acceptTermOfService('pp')">{{ $t("search.integration.privatePolicy") }}</a>
                            {{ $t("message.and") }}
                            <a @click="acceptTermOfService('tac')">{{
                                $t("search.integration.termsAndCondition")
                            }}</a></b
                        >
                    </div>
                </div>

                <!-- Step 2: Business (Portfolio) secimi -->
                <div class="select-business-body" v-else-if="step === 'select-business'">
                    <a-spin :spinning="loadingBusinesses">
                        <BusinessList
                            :businesses="businesses"
                            v-model="selectedBusiness"
                            :show-personal="true"
                        />
                    </a-spin>
                </div>

                <!-- Step 3: Ad Account secimi -->
                <div class="select-account-body" v-else-if="step === 'select-account'">
                    <div class="step-back" v-if="selectedBusiness">
                        <a @click="goBackToBusinesses">
                            <i class="material-icons">arrow_back</i>
                            {{ $t("metaInsights.changeBusiness") }}
                        </a>
                        <span class="muted">{{ selectedBusiness.name }}</span>
                    </div>
                    <a-spin :spinning="loadingAccounts">
                        <AdAccountList :accounts="adAccounts" v-model="selectedAdAccount" />
                    </a-spin>
                </div>
            </div>
        </a-form>

        <div class="form-footer">
            <div class="actions">
                <SupsisButton ghost @click="handleCancel">{{ $t("wp.cancel") }}</SupsisButton>

                <SupsisButton
                    v-if="step === 'oauth'"
                    primary
                    @click="handleSubmit"
                    :disabled="!termOfService || loading"
                >
                    {{ $t("metaInsights.connectToMetaInsights") }}
                </SupsisButton>

                <SupsisButton
                    v-if="step === 'select-business'"
                    primary
                    @click="handleSelectBusiness"
                    :disabled="!selectedBusiness || loadingAccounts"
                >
                    {{ $t("metaInsights.continue") }}
                </SupsisButton>

                <SupsisButton
                    v-if="step === 'select-account'"
                    primary
                    @click="handleCreate"
                    :disabled="!selectedAdAccount || creating"
                >
                    {{ $t("metaInsights.createChannel") }}
                </SupsisButton>
            </div>
        </div>

        <TermOfService
            v-if="termOfServiceModal"
            :type="termOfServiceModal"
            @agree="acceptedTermOfService"
            @cancel="termOfServiceModal = null"
        />
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import CONFIG from "../../../../config/config";
import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";
import { first, map, timeout } from "rxjs/operators";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import TermOfService from "../../../../components/TermOfService.vue";
import { generateRandomId } from "../../../../utils/session";
import AdAccountList from "./AdAccountList.vue";
import BusinessList from "./BusinessList.vue";
import { INSIGHTS_API } from "../../../../api/meta";

export default {
    name: "CreateMetaInsightsChannel",
    props: {
        channel: {
            type: Object,
            default: () => {},
        },
    },
    components: {
        TermOfService,
        Modal2,
        SupsisButton,
        AdAccountList,
        BusinessList,
    },
    data() {
        return {
            form: {
                channelName: "Meta Insights",
            },
            step: "oauth",
            loading: false,
            loadingBusinesses: false,
            loadingAccounts: false,
            creating: false,
            clone: _.cloneDeep(this.channel),
            submitClicked: false,
            tokenInfo: null,
            businesses: [],
            selectedBusiness: null,
            adAccounts: [],
            selectedAdAccount: null,
            termOfService: false,
            termOfServiceModal: false,
            requestId: null,
            window: null,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site && state.site.site._id,
            userId: (state) =>
                (state.auth.user && state.auth.user._id) || (state.auth.adminUser && state.auth.adminUser._id),
        }),
    },
    methods: {
        ...mapActions({
            createChannel: "channel/createChannel",
        }),
        acceptTermOfService(type) {
            this.termOfServiceModal = type || "pp";
        },
        acceptedTermOfService() {
            this.termOfServiceModal = null;
            this.termOfService = true;
        },
        getRedirectURL() {
            return `${CONFIG.SOCKET_URL}/webhook/oauth2/facebook`;
        },
        getErrorStatus(fieldName) {
            return this.form[fieldName].length < 1 && this.submitClicked ? "error" : "";
        },
        getErrorMessage(fieldName) {
            return this.form[fieldName].length < 1 && this.submitClicked ? this.$t("message.requiredChannel") : "";
        },
        handleCancel() {
            this.$emit("cancel");
        },
        startOauth() {
            const FACEBOOK_APP_ID = "7174288719300175";
            this.loading = true;
            const redirectUrl = this.getRedirectURL();
            // Insights icin gerekli scope'lar - meta-leads'ten farkli olarak ads_read + read_insights
            const scopes = [
                "ads_read",
                "ads_management",
                "business_management",
                "read_insights",
                "public_profile",
            ];
            const scope = scopes.join(",");

            this.requestId = generateRandomId(4);
            const state = this.siteId + "_" + this.userId + "_meta-insights_" + this.requestId;
            const url = `https://www.facebook.com/v21.0/dialog/oauth?client_id=${FACEBOOK_APP_ID}&redirect_uri=${redirectUrl}&state=${state}&scope=${scope}`;
            this.listenSocket(this.requestId);
            this.window = window.open(url, "_blank");
        },
        listenSocket(requestId) {
            socketMessageStream
                .pipe(
                    first((x) => x.cmd === "facebook_callback_response"),
                    map((x) => x.data),
                    timeout(60000 * 10)
                )
                .toPromise()
                .then(async (data) => {
                    if (this.requestId !== requestId) return;
                    this.loading = false;
                    if (this.window) this.window.close();
                    if (!data || !data.tokenInfo || !_.get(data, "tokenInfo.accessToken.access_token")) {
                        this.$message.error(this.$t("metaInsights.failedToConnect"));
                        return;
                    }
                    this.tokenInfo = data.tokenInfo;
                    this.$message.success(this.$t("metaInsights.connectedSuccess"));
                    await this.fetchBusinesses();
                })
                .catch(() => {
                    if (this.requestId !== requestId) return;
                    this.loading = false;
                    if (this.window) this.window.close();
                    this.$message.error(this.$t("metaInsights.failedToConnect"));
                });
        },
        async fetchBusinesses() {
            const token = _.get(this.tokenInfo, "accessToken.access_token");
            if (!token) return;
            this.loadingBusinesses = true;
            this.step = "select-business";
            try {
                const res = await INSIGHTS_API.listBusinessesByToken(token);
                this.businesses = (res && res.data) || [];
            } catch (err) {
                console.error(err);
                this.$message.error(this.$t("metaInsights.failedToListBusinesses"));
            } finally {
                this.loadingBusinesses = false;
            }
        },
        async handleSelectBusiness() {
            if (!this.selectedBusiness) return;
            await this.fetchAdAccounts();
        },
        async fetchAdAccounts() {
            const token = _.get(this.tokenInfo, "accessToken.access_token");
            if (!token || !this.selectedBusiness) return;
            this.loadingAccounts = true;
            this.step = "select-account";
            this.adAccounts = [];
            this.selectedAdAccount = null;
            try {
                let res;
                if (this.selectedBusiness.id === "personal") {
                    res = await INSIGHTS_API.listPersonalAdAccountsByToken(token);
                } else {
                    res = await INSIGHTS_API.listAdAccountsByBusinessId(token, this.selectedBusiness.id);
                }
                this.adAccounts = (res && res.data) || [];
                if (this.adAccounts.length === 1) {
                    this.selectedAdAccount = this.adAccounts[0];
                }
            } catch (err) {
                console.error(err);
                this.$message.error(this.$t("metaInsights.failedToListAdAccounts"));
            } finally {
                this.loadingAccounts = false;
            }
        },
        goBackToBusinesses() {
            this.step = "select-business";
            this.adAccounts = [];
            this.selectedAdAccount = null;
        },
        handleCreate() {
            if (!this.selectedAdAccount) return;
            this.creating = true;
            const data = {
                type: "meta-insights",
                name: this.form.channelName || this.selectedAdAccount.name || "Meta Insights",
                payload: {
                    tokenInfo: this.tokenInfo,
                    selectedBusiness:
                        this.selectedBusiness && this.selectedBusiness.id !== "personal"
                            ? this.selectedBusiness
                            : null,
                    selectedAdAccount: this.selectedAdAccount,
                    adAccounts: this.adAccounts,
                },
            };
            this.createChannel(data)
                .then((channel) => {
                    this.$message.success(this.$t("message.channelCreatedSuccessfully"));
                    this.$emit("ok", channel);
                    this.$router.push(`/integrations/meta-insights/${channel._id}/detail/overview`);
                })
                .catch((err) => {
                    console.error(err);
                    this.$message.error(this.$t("message.failedToCreateChannel"));
                })
                .finally(() => {
                    this.creating = false;
                });
        },
        handleSubmit() {
            this.submitClicked = true;
            if (this.form.channelName.length < 1) {
                if (this.$refs.channelName) this.$refs.channelName.focus();
                return;
            }
            this.startOauth();
        },
    },
    mounted() {},
    beforeDestroy() {},
    watch: {},
};
</script>

<style lang="scss" scoped>
#new-meta-insights-channel-form {
    min-height: 250px;
    display: flex;
    flex-direction: column;

    .form-wrapper {
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 5px;
            height: 75px;
        }
    }

    .form-footer {
        display: flex;
        justify-content: flex-end;
        padding: 10px;
        margin-top: 20px;
        background: var(--color-bg-default);
        border-top: 1px solid var(--color-border-default);

        .actions {
            display: flex;
            gap: 10px;
        }
    }

    .connect-body {
        display: flex;
        flex-direction: column;
        gap: 5px;
        justify-content: center;

        .requirements {
            display: flex;
            flex-direction: column;
            gap: 5px;

            div {
                display: flex;
                gap: 5px;
                align-items: center;
                justify-content: flex-start;

                .material-icons {
                    color: var(--color-text-info-default);
                    font-size: 13px;
                }
            }
        }
    }

    .select-business-body,
    .select-account-body {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-height: 200px;
    }

    .step-back {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;

        a {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            color: #1f6feb;

            i {
                font-size: 14px;
            }
        }

        .muted {
            color: #6b7280;
        }
    }
}
</style>

<style lang="scss">
.new-meta-insights-channel {
    .custom-modal-body {
        padding: 0px !important;
    }
}
</style>
